import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { ShieldCheck, Lock, PlugZap, RefreshCcw, Copy, Loader2, AlertTriangle, CheckCircle2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/purolink-sync`;
const DEFAULT_URL = 'https://purolink.store/api/partner/v1';
const fmt = (d) => (d ? new Date(d).toLocaleString('es-ES') : '—');
const errMsg = (e) => e.response?.data?.detail || e.message;

const StatusBadge = ({ s }) => {
  if (s.running) return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100" data-testid="purolink-status-badge">Sincronizando…</Badge>;
  if (s.last_status === 'error') return <Badge className="bg-red-100 text-red-700 hover:bg-red-100" data-testid="purolink-status-badge">Error</Badge>;
  if (s.last_status === 'ok') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="purolink-status-badge">OK</Badge>;
  return <Badge variant="secondary" data-testid="purolink-status-badge">Sin sincronizar</Badge>;
};

const ConnectionCard = ({ s, onSaved }) => {
  const [token, setToken] = useState('');
  const [baseUrl, setBaseUrl] = useState(s.base_url);
  const [busy, setBusy] = useState(false);
  const [ip, setIp] = useState(null);

  useEffect(() => { axios.get(`${API}/server-ip`).then((r) => setIp(r.data.ip)).catch(() => setIp('No disponible')); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(API, { ...(token.trim() ? { token: token.trim() } : {}), base_url: baseUrl });
      setToken('');
      toast.success('Configuración guardada');
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/test`);
      toast.success(`Conexión correcta${r.data.response?.partner ? ` · partner: ${r.data.response.partner}` : ''}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success('IP copiada')).catch(() => toast.error('No se pudo copiar'));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Lock className="h-4 w-4" /> Conexión privada con Purolink</CardTitle>
        <CardDescription>Uso interno: solo este servidor llama a Purolink. El token nunca se muestra ni se comparte con clientes.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Token de Purolink (API Partners)</Label>
            <Input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)}
              placeholder={s.has_token ? `Guardado: ${s.token_hint} — pega uno nuevo para cambiarlo` : 'pk_live_...'} data-testid="purolink-token-input" />
            <p className="text-xs text-slate-500 mt-1">Se guarda cifrado. Tras regenerarlo en Purolink, pégalo aquí y pulsa Guardar.</p>
          </div>
          <div>
            <Label>URL de la API</Label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} data-testid="purolink-base-url-input" />
            <p className="text-xs text-slate-500 mt-1">
              No la cambies: debe ser <code>{DEFAULT_URL}</code>
              {baseUrl !== DEFAULT_URL && (
                <button type="button" onClick={() => setBaseUrl(DEFAULT_URL)} className="ml-2 text-primary underline" data-testid="purolink-reset-url-btn">Restablecer</button>
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="purolink-save-btn">Guardar</Button>
          <Button variant="outline" onClick={test} disabled={busy || !s.has_token} data-testid="purolink-test-btn"><PlugZap className="h-4 w-4 mr-1" /> Probar conexión</Button>
        </div>
        <div className="border rounded-lg p-3 bg-slate-50 text-sm flex flex-wrap items-center gap-2" data-testid="purolink-server-ip-box">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          <span>IP pública de este servidor:</span>
          <code className="font-mono font-semibold" data-testid="purolink-server-ip">{ip || '…'}</code>
          {ip && ip !== 'No disponible' && <Button size="sm" variant="ghost" onClick={() => copy(ip)} data-testid="purolink-copy-ip-btn"><Copy className="h-3.5 w-3.5" /></Button>}
          <span className="text-slate-500">→ en Purolink: API Partners → tu token «Keypubli» → «IPs permitidas», pega esta IP y guarda. Así ninguna otra IP podrá usar el token.</span>
        </div>
      </CardContent>
    </Card>
  );
};

const SyncCard = ({ s, onChange, withSeo }) => {
  const [seoCheck, setSeoCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const checkSeo = async () => {
    setChecking(true);
    try { const r = await axios.post(`${API}/check-seo`); setSeoCheck(r.data); }
    catch (e) { toast.error(errMsg(e)); } finally { setChecking(false); }
  };
  const toggle = async (v) => {
    try { await axios.put(API, { enabled: v }); toast.success(v ? 'Sincronización automática activada' : 'Sincronización pausada'); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const run = async (kind) => {
    try { await axios.post(`${API}/run`, { kind }); toast.success('Sincronización iniciada'); setTimeout(onChange, 1500); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const p = s.progress;
  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><RefreshCcw className="h-4 w-4" /> Sincronización automática <StatusBadge s={s} /></CardTitle>
          <CardDescription>Cada hora trae de Purolink lo nuevo y lo modificado. Una vez al día marca como «no disponibles» los medios que ya no están en Purolink (no se borran).</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm">{s.enabled ? 'Activada' : 'Pausada'}</span>
          <Switch checked={s.enabled} onCheckedChange={toggle} disabled={!s.has_token} data-testid="purolink-enabled-switch" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">Última sincronización</div><div className="font-medium" data-testid="purolink-last-sync">{fmt(s.last_sync_at)}</div></div>
          <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">Última carga completa</div><div className="font-medium">{fmt(s.last_full_at)}</div></div>
          <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">Última revisión de retirados</div><div className="font-medium">{fmt(s.last_ids_at)}</div></div>
        </div>
        {s.running && p && (
          <div className="flex items-center gap-2 text-sm text-blue-700" data-testid="purolink-progress"><Loader2 className="h-4 w-4 animate-spin" /> {p.kind === 'full' ? 'Carga completa' : 'Cambios'}: página {p.page} de {p.total_pages}</div>
        )}
        {s.last_status === 'error' && s.last_error && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="purolink-last-error"><AlertTriangle className="h-4 w-4 mt-0.5" /> {s.last_error}</div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run('auto')} disabled={!s.has_token || s.running} className="bg-slate-900 hover:bg-slate-800" data-testid="purolink-run-now-btn">
            <RefreshCcw className="h-4 w-4 mr-1" /> Sincronizar ahora
          </Button>
          <Button variant="outline" onClick={() => run('full')} disabled={!s.has_token || s.running} data-testid="purolink-run-full-btn">Sincronización completa</Button>
          <Button variant="outline" onClick={() => run('ids')} disabled={!s.has_token || s.running} data-testid="purolink-run-ids-btn">Revisar retirados</Button>
          <Button variant="outline" onClick={checkSeo} disabled={!s.has_token || checking} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="purolink-check-seo-btn">
            {checking ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Comprobar métricas SEO
          </Button>
        </div>
        <div className="text-sm text-slate-600" data-testid="purolink-products-with-seo">Medios con métricas SEO en KeyPubli: <b>{(withSeo ?? 0).toLocaleString('es-ES')}</b></div>
        {seoCheck && (
          <div className={`text-sm rounded-lg p-3 border ${seoCheck.has_seo_metrics ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="purolink-seo-check-result">
            {seoCheck.has_seo_metrics
              ? <p><b>Purolink SÍ envía las métricas SEO</b> (ejemplo: {seoCheck.website}, TF {seoCheck.seo_parsed?.tf ?? '—'}, RD {seoCheck.seo_parsed?.rd ?? '—'}). Si aún no salen, pulsa «Sincronización completa».</p>
              : <p><b>Purolink NO está enviando «seo_metrics»</b> en la API de Partners. Pásale esto a Purolink.</p>}
            <p className="text-xs mt-1 break-all">Campos recibidos: {seoCheck.fields.join(', ') || '—'}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AlertCard = ({ s, onSaved }) => {
  const [email, setEmail] = useState(s.alert_email || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await axios.put(API, { alert_email: email }); toast.success('Email de avisos guardado'); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/test-alert`); toast.success(`Email de prueba enviado a ${r.data.to}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Aviso por email</CardTitle>
        <CardDescription>Te enviamos un email si la sincronización falla o el token deja de funcionar. El mismo error solo se avisa una vez, hasta que vuelva a sincronizar bien.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[260px]">
          <Label>Email que recibe los avisos</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="purolink-alert-email-input" />
        </div>
        <Button onClick={save} disabled={busy || !email.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="purolink-alert-save-btn">Guardar</Button>
        <Button variant="outline" onClick={test} disabled={busy} data-testid="purolink-alert-test-btn">Enviar email de prueba</Button>
        {s.last_alert_at && <span className="text-xs text-slate-500 w-full" data-testid="purolink-last-alert">Último aviso enviado: {fmt(s.last_alert_at)}</span>}
      </CardContent>
    </Card>
  );
};

const RunsCard = ({ runs }) => (
  <Card>
    <CardHeader className="pb-3"><CardTitle className="text-base">Historial</CardTitle></CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="purolink-runs-table">
          <thead><tr className="text-left text-xs text-slate-500 border-b">
            <th className="py-2 pr-3">Fecha</th><th className="pr-3">Tipo</th><th className="pr-3">Estado</th><th className="pr-3 text-right">Recibidos</th>
            <th className="pr-3 text-right">Nuevos</th><th className="pr-3 text-right">Modificados</th><th className="pr-3 text-right">Sin cambios</th><th className="pr-3 text-right">Retirados</th><th className="pr-3 text-right">Con SEO</th><th className="text-right">Errores</th>
          </tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`purolink-run-${r.id}`} title={r.message || (r.error_details || []).join('\n')}>
                <td className="py-2 pr-3 whitespace-nowrap">{fmt(r.started_at)}</td>
                <td className="pr-3">{r.baseline ? 'Carga inicial (sin marcas)' : ({ full: 'Completa', delta: 'Cambios', ids: 'Retirados' }[r.kind] || r.kind)}</td>
                <td className="pr-3">{r.status === 'ok' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : r.status === 'running' ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <span className="text-red-600 text-xs">{r.message || 'Error'}</span>}</td>
                <td className="pr-3 text-right">{r.received ?? 0}</td>
                <td className="pr-3 text-right font-medium text-primary">{r.new ?? 0}</td>
                <td className="pr-3 text-right text-amber-700">{r.updated ?? 0}</td>
                <td className="pr-3 text-right text-slate-500">{r.unchanged ?? 0}</td>
                <td className="pr-3 text-right text-red-600">{r.removed ?? 0}</td>
                <td className="pr-3 text-right text-red-700">{r.with_seo ?? '—'}</td>
                <td className="text-right">{r.errors ?? 0}</td>
              </tr>
            ))}
            {runs.length === 0 && <tr><td colSpan={10} className="text-center text-slate-500 py-6">Aún no hay sincronizaciones</td></tr>}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

export const PurolinkSyncTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData((await axios.get(API)).data); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.settings.running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data?.settings.running, load]);

  if (!data) return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  return (
    <div className="space-y-6" data-testid="purolink-sync-tab">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Sincronización Purolink</h2>
        <p className="text-sm text-slate-500">Mantiene los medios de KeyPubli actualizados desde Purolink (emails, PC, PVP…). API privada: no se comparte con clientes.</p>
      </div>
      <ConnectionCard s={data.settings} onSaved={load} />
      <SyncCard s={data.settings} onChange={load} withSeo={data.products_with_seo} />
      <AlertCard s={data.settings} onSaved={load} />
      <RunsCard runs={data.runs} />
    </div>
  );
};
